import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Collapse,
  IconButton,
  TextareaAutosize,
  Tabs,
  Tab,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  Upload,
  Download,
  TuneRounded,
  HighlightOff
} from '@mui/icons-material';
import CarrierCard from '../CarrierCard/CarrierCard';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';

const SearchDashboard = ({
  onSearch,
  onBatchSearch,
  clearBatchResults,
  loading,
  error,
  carrierData,
  showBasicInfo,
  onToggleView
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('auto');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [batchInput, setBatchInput] = useState('');
  const [filters, setFilters] = useState({
    skipBrokers: false,
    skipInactive: false,
    showInactiveOnly: false,
    activeDuration: 0,
    resultLimit: 10,
    state: '',
    states: []
  });

  const handleSearch = () => {
    if (searchTerm.trim() || searchType === 'range') {
      onSearch(searchTerm.trim(), searchType, filters);
    }
  };

  const handleBatchSearch = () => {
    if (batchInput.trim()) {
      onBatchSearch(batchInput.trim(), filters);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (activeTab === 0) {
        handleSearch();
      } else {
        handleBatchSearch();
      }
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearSearch = () => {
    setSearchTerm('');
    setBatchInput('');
    setFilters({
      skipBrokers: false,
      skipInactive: false,
      showInactiveOnly: false,
      activeDuration: 0,
      resultLimit: 10,
      state: '',
      states: []
    });
  };

  const quickSearch = (type, value) => {
    setSearchType(type);
    setSearchTerm(value);
    setTimeout(() => handleSearch(), 100);
  };

  const detectSearchType = (input) => {
    const trimmed = input.trim();

    if (/^MC-?\d{6,8}$/i.test(trimmed) || /^\d{6,8}$/.test(trimmed)) {
      return 'mc';
    }
    if (/^\d{7,8}$/.test(trimmed)) {
      return 'dot';
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return 'email';
    }
    if (/^\d+-\d+$/.test(trimmed)) {
      return 'range';
    }
    if (/^[a-zA-Z\s.,&]+$/.test(trimmed) && trimmed.length > 2) {
      return 'name';
    }
    return 'auto';
  };

  const searchTypes = [
    { id: 'auto', label: 'Auto Detect', placeholder: 'Enter MC, DOT, email, or name...', example: 'MC-123456' },
    { id: 'mc', label: 'MC Number', placeholder: 'Enter MC number...', example: '123456' },
    { id: 'dot', label: 'DOT Number', placeholder: 'Enter DOT number...', example: '2874689' },
    { id: 'email', label: 'Email', placeholder: 'Enter email address...', example: 'carrier@company.com' },
    { id: 'name', label: 'Company Name', placeholder: 'Enter company name...', example: 'ABC Trucking' },
    { id: 'range', label: 'Number Range', placeholder: 'e.g., 100000-100050', example: '100000-100050' }
  ];

  const quickExamples = [
    { type: 'mc', label: 'MC Search', value: '123456' },
    { type: 'dot', label: 'DOT Search', value: '2874689' },
    { type: 'range', label: 'Range Search', value: '100000-100010' },
    { type: 'email', label: 'Email Search', value: 'carrier@example.com' },
    { type: 'name', label: 'Name Search', value: 'ABC Trucking' }
  ];

  const batchExamples = `123456
2874689
MC-654321
carrier@example.com
ABC Trucking
100000-100005`;
// inside SearchDashboard component
 const exportCSV = (payload) => {
   // Normalize to array of carrier rows
   const rows = payload?.searchType === 'batch'
    ? (payload.batchResults || [])
    : (payload ? [payload] : []);

  if (!rows.length) return;

   // Choose columns
   const headers = [
    'Legal Name','DBA','DOT','MC','State(Physical)','State(Mailing)',
    'Phone','Email','Status','Carrier Operation','Power Units','Drivers','Active Duration',
    'Physical Address','Mailing Address'
  ];

   const esc = (v) => {
     const s = (v === undefined || v === null) ? '' : String(v);
return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const lines = [headers.join(',')];
   rows.forEach(c => {
     lines.push([
       c.legalName, c.dbaName, c.dotNumber, c.mcNumber,
       c.phyState || '', c.mailState || '',
       c.phone, c.email, c.status, c.carrierOperation,
       c.powerUnits, c.totalDrivers,
       c.activeDuration,
       c.physicalAddress, c.mailingAddress
     ].map(esc).join(','));
   });

   const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
   a.href = url;
   a.download = `carriers-${ts}.csv`;
   document.body.appendChild(a);   a.click();
   document.body.removeChild(a);
   URL.revokeObjectURL(url);
 };
const exportPDF = async (payload, selectedStates = []) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  // Normalize rows
  const rows = payload?.searchType === 'batch'
    ? (payload.batchResults || [])
    : (payload ? [payload] : []);

  if (!rows.length) return;

  // Group by state (prefer physical, then mailing, else 'Unknown')
  const groupKey = (c) => (c.phyState || c.mailState || 'Unknown').toUpperCase();
  const grouped = {};
  rows.forEach(c => {
    const key = groupKey(c);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  // If multi-select applied, order by selectedStates first; else alpha
  const order = (Array.isArray(selectedStates) && selectedStates.length)
    ? [...selectedStates.map(s => s.toUpperCase()), ...Object.keys(grouped).filter(k => !selectedStates.map(s=>s.toUpperCase()).includes(k)).sort()]
    : Object.keys(grouped).sort();

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const columns = [
    { header: 'Legal Name', dataKey: 'legalName' },
    { header: 'DBA', dataKey: 'dbaName' },
    { header: 'DOT', dataKey: 'dotNumber' },
    { header: 'MC', dataKey: 'mcNumber' },
    { header: 'Phone', dataKey: 'phone' },
    { header: 'Email', dataKey: 'email' },
    { header: 'Op', dataKey: 'carrierOperation' },
    { header: 'Units', dataKey: 'powerUnits' },
    { header: 'Drivers', dataKey: 'totalDrivers' },
    { header: 'Status', dataKey: 'status' },
  ];

  let firstPage = true;

  for (const st of order) {
    const data = grouped[st];
    if (!data || !data.length) continue;

    if (!firstPage) doc.addPage();
    firstPage = false;

    // Title
    doc.setFontSize(16);
    doc.text(`Carriers — ${st}`, 40, 40);

    // Subline (date & count)
    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}  |  Records: ${data.length}`, 40, 58);

    // Table
    autoTable(doc, {
      startY: 75,
      head: [columns.map(c => c.header)],
      body: data.map(c => columns.map(col => c[col.dataKey] ?? '')),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fontStyle: 'bold' },
      margin: { left: 40, right: 40 },
      tableWidth: 'auto',
      didDrawPage: (d) => {
        // Footer page number
        const str = `Page ${doc.getCurrentPageInfo().pageNumber}`;
        doc.setFontSize(9);
        doc.text(str, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 20);
      }
    });

    // Optional: Addresses table (second table on same page)
     autoTable(doc, {
       startY: doc.lastAutoTable.finalY + 12,
      head: [['Physical Address', 'Mailing Address']],
      body: data.map(c => [c.physicalAddress || '', c.mailingAddress || '']),
      styles: { fontSize: 8, cellPadding: 4 },
      margin: { left: 40, right: 40 },
     });
  }

  const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
  doc.save(`carriers-statewise-${ts}.pdf`);
};

  const durationMarks = [
    { value: 0, label: 'Any' },
    { value: 6, label: '6M' },
    { value: 12, label: '1Y' },
    { value: 24, label: '2Y' },
    { value: 60, label: '5Y' }
  ];

  const hasActiveFilters = filters.skipBrokers || filters.skipInactive ||
    filters.showInactiveOnly || filters.activeDuration > 0 ||
    filters.resultLimit !== 10;

  const isBatchActive = carrierData && carrierData.searchType === 'batch';

  return (
    <Box sx={{
      px: { xs: 1, sm: 2, md: 3 },
      py: 2,
      maxWidth: 1400,
      mx: 'auto'
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Carrier Search
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search and discover carrier information across multiple sources
        </Typography>
      </Box>

      {/* Main Search Card */}
      <Card sx={{
        mb: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid rgba(102,126,234,0.1)',
        overflow: 'hidden'
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)',
          borderBottom: '1px solid rgba(102,126,234,0.1)'
        }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                height: 3
              },
              '& .MuiTab-root': {
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                transition: 'all 0.3s ease'
              }
            }}
          >
            <Tab label="Single Search" icon={<SearchIcon sx={{ mr: 0.5 }} />} iconPosition="start" />
            <Tab label="Batch Search" icon={<Upload sx={{ mr: 0.5 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 0 && (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
              {/* Quick Search Chips */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.primary'
                  }}
                >
                  <SearchIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  Quick Templates
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {quickExamples.map((item, index) => (
                    <Chip
                      key={index}
                      label={item.label}
                      size={isMobile ? "small" : "medium"}
                      variant="outlined"
                      onClick={() => quickSearch(item.type, item.value)}
                      clickable
                      sx={{
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(102,126,234,0.1)',
                          borderColor: 'primary.main',
                          color: 'primary.main'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Search Input Section */}
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={3} md={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontWeight: 500 }}>Search Type</InputLabel>
                    <Select
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                      label="Search Type"
                      sx={{
                        fontWeight: 500,
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover fieldset': {
                            borderColor: 'primary.main'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2
                          }
                        }
                      }}
                    >
                      {searchTypes.map(type => (
                        <MenuItem key={type.id} value={type.id} sx={{ fontWeight: 500 }}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={searchTypes.find(t => t.id === searchType)?.placeholder}
                    value={searchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchTerm(value);
                      if (searchType === 'auto' && value.trim()) {
                        const detectedType = detectSearchType(value);
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'primary.main', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchTerm('')}
                            edge="end"
                          >
                            <HighlightOff sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      searchType === 'auto' && searchTerm.trim()
                        ? `🔍 Detected: ${detectSearchType(searchTerm)}`
                        : `💡 ${searchTypes.find(t => t.id === searchType)?.example}`
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.2s ease',
                        '&:hover fieldset': {
                          borderColor: 'primary.main'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={2.5}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleSearch}
                      disabled={loading || (!searchTerm.trim() && searchType !== 'range')}
                      startIcon={<SearchIcon />}
                      fullWidth
                      sx={{
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        transition: 'all 0.3s ease',
                        '&:hover:not(:disabled)': {
                          boxShadow: '0 8px 24px rgba(102,126,234,0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                    <IconButton
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      color={showAdvanced ? "primary" : "default"}
                      size="small"
                      sx={{
                        border: '1px solid',
                        borderColor: showAdvanced ? 'primary.main' : 'divider',
                        transition: 'all 0.2s ease'
                      }}
                      title="Advanced Filters"
                    >
                      <TuneRounded />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {/* Batch Search Description */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.primary'
                  }}
                >
                  <Upload sx={{ fontSize: 18, color: 'primary.main' }} />
                  Batch Input
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Enter multiple search terms (one per line) to process them in bulk
                </Typography>

                {/* Textarea with Modern Styling */}
                <Paper
                  elevation={0}
                  sx={{
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 0,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    background: 'rgba(102,126,234,0.02)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 4px 12px rgba(102,126,234,0.1)'
                    },
                    '&:focus-within': {
                      borderColor: 'primary.main',
                      boxShadow: '0 4px 12px rgba(102,126,234,0.15)'
                    }
                  }}
                >
                  <TextareaAutosize
                    minRows={isMobile ? 5 : 7}
                    maxRows={isMobile ? 8 : 12}
                    placeholder={`Enter multiple search terms, one per line:\n\n${batchExamples}`}
                    value={batchInput}
                    onChange={(e) => setBatchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: 'none',
                      fontSize: '14px',
                      fontFamily: '"Roboto Mono", monospace',
                      resize: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      outline: 'none'
                    }}
                  />
                </Paper>

                {/* Batch Info */}
                <Box sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}>
                  <Box sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    background: 'rgba(102,126,234,0.1)',
                    borderRadius: 1,
                    fontWeight: 600,
                    color: 'primary.main'
                  }}>
                    {batchInput.split('\n').filter(l => l.trim()).length} lines
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Ready to search
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Button
                  variant="contained"
                  onClick={handleBatchSearch}
                  disabled={loading || !batchInput.trim()}
                  startIcon={<SearchIcon />}
                  fullWidth
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    transition: 'all 0.3s ease',
                    '&:hover:not(:disabled)': {
                      boxShadow: '0 8px 24px rgba(102,126,234,0.4)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {loading ? 'Processing...' : `Search ${batchInput.split('\n').filter(l => l.trim()).length} Terms`}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => setBatchInput(batchExamples)}
                  fullWidth={isMobile}
                  sx={{
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  Load Example
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  color="error"
                  onClick={() => {
                    if (clearBatchResults) clearBatchResults();
                    setBatchInput('');
                  }}
                  disabled={!isBatchActive}
                  fullWidth={isMobile}
                  sx={{
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  Clear
                </Button>

                <IconButton
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  color={showAdvanced ? "primary" : "default"}
                  sx={{
                    border: '1px solid',
                    borderColor: showAdvanced ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease',
                    height: 40,
                    width: 40
                  }}
                  title="Advanced Filters"
                >
                  <TuneRounded />
                </IconButton>
              </Stack>
            </Box>
          )}

          {/* Advanced Filters */}
          <Collapse in={showAdvanced}>
            <Box sx={{
              mt: 3,
              pt: 3,
              borderTop: '2px solid',
              borderColor: 'divider'
            }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.primary'
                }}
              >
                <TuneRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                Advanced Filters
              </Typography>

              <Grid container spacing={2}>
                {/* Carrier Type */}
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, background: 'rgba(102,126,234,0.03)', borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Carrier Type
                    </Typography>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipBrokers}
                            onChange={(e) => handleFilterChange('skipBrokers', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Exclude Brokers</Typography>}
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipInactive}
                            onChange={(e) => handleFilterChange('skipInactive', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Active Only</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                {/* States Selection */}
                <Grid item xs={12} sm={6} md={5}>
                  <Paper elevation={0} sx={{ p: 2, background: 'rgba(102,126,234,0.03)', borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Filter by States
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>States</InputLabel>
                      <Select
                        multiple
                        value={filters.states}
                        label="States"
                        onChange={(e) => handleFilterChange('states', e.target.value)}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {selected.slice(0, 3).map(s => (
                              <Chip key={s} label={s} size="small" />
                            ))}
                            {selected.length > 3 && (
                              <Chip label={`+${selected.length - 3}`} size="small" />
                            )}
                          </Box>
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main'
                            }
                          }
                        }}
                      >
                        {[
                          'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
                        ].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>

                {/* Active Duration */}
                <Grid item xs={12} sm={6} md={2}>
                  <Paper elevation={0} sx={{ p: 2, background: 'rgba(102,126,234,0.03)', borderRadius: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span>Min Active</span>
                      <Chip
                        label={filters.activeDuration === 0 ? 'Any' : `${filters.activeDuration}M`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Typography>
                    <Slider
                      size="small"
                      value={filters.activeDuration}
                      onChange={(e, newValue) => handleFilterChange('activeDuration', newValue)}
                      min={0}
                      max={60}
                      step={6}
                      marks={durationMarks}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value === 0 ? 'Any' : `${value}M`}
                    />
                  </Paper>
                </Grid>

                {/* Results Limit */}
                <Grid item xs={12} sm={6} md={2}>
                  <Paper elevation={0} sx={{ p: 2, background: 'rgba(102,126,234,0.03)', borderRadius: 1.5 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontWeight: 500 }}>Max Results</InputLabel>
                      <Select
                        value={filters.resultLimit}
                        onChange={(e) => handleFilterChange('resultLimit', e.target.value)}
                        label="Max Results"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>
              </Grid>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    ACTIVE FILTERS
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {filters.skipBrokers && (
                      <Chip
                        label="🚫 Brokers Excluded"
                        size="small"
                        onDelete={() => handleFilterChange('skipBrokers', false)}
                        color="primary"
                      />
                    )}
                    {filters.skipInactive && (
                      <Chip
                        label="✓ Active Only"
                        size="small"
                        onDelete={() => handleFilterChange('skipInactive', false)}
                        color="success"
                      />
                    )}
                    {filters.activeDuration > 0 && (
                      <Chip
                        label={`📅 Min ${filters.activeDuration}M`}
                        size="small"
                        onDelete={() => handleFilterChange('activeDuration', 0)}
                        color="warning"
                      />
                    )}
                    {filters.states.length > 0 && (
                      <Chip
                        label={`📍 ${filters.states.length} State${filters.states.length > 1 ? 's' : ''}`}
                        size="small"
                        onDelete={() => handleFilterChange('states', [])}
                      />
                    )}
                    {filters.resultLimit !== 10 && (
                      <Chip
                        label={`📊 Max ${filters.resultLimit}`}
                        size="small"
                        onDelete={() => handleFilterChange('resultLimit', 10)}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          severity={error.includes('found') ? 'warning' : 'error'}
          sx={{
            mb: 3,
            fontWeight: 500,
            borderRadius: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Export Buttons */}
      {carrierData && (
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1}
          sx={{ mb: 3, justifyContent: 'flex-end' }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={() => exportCSV(carrierData)}
            sx={{ fontWeight: 600 }}
          >
            Export CSV
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Download />}
            onClick={() => exportPDF(carrierData, filters.states)}
            sx={{
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            Export PDF
          </Button>
        </Stack>
      )}

      {/* Results Section */}
      <Box sx={{ minHeight: '400px' }}>
        {carrierData && carrierData.searchType === 'batch' ? (
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
          />
        ) : loading ? (
          <SkeletonLoader />
        ) : carrierData ? (
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
          />
        ) : (
          <Card sx={{
            textAlign: 'center',
            py: { xs: 6, sm: 8 },
            background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            transition: 'all 0.3s ease'
          }}>
            <SearchIcon sx={{
              fontSize: { xs: 40, sm: 56 },
              color: 'primary.main',
              mb: 2,
              opacity: 0.6
            }} />
            <Typography
              variant={isMobile ? "body1" : "h6"}
              sx={{
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              {activeTab === 0 ? '🔍 Ready to Search' : '📋 Ready for Batch Processing'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {activeTab === 0
                ? 'Enter search criteria to discover carriers'
                : 'Paste multiple search terms to process in bulk'}
            </Typography>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default SearchDashboard;